export interface ToolStep {
  title: string;
  description: string;
}

export type ToolOfflineMode = 'ready' | 'limited' | 'online-required';

export interface ToolDefinition {
  id: string;
  slug: string;
  name: string;
  description: string;
  category: 'Generators' | 'PDF' | 'Image' | 'Design' | 'Developer';
  categoryColor: 'violet' | 'coral' | 'cyan' | 'amber' | 'green';
  icon: string;
  gradient: string;
  isNew: boolean;
  badge: string | null;
  metadataTitle: string;
  metadataDescription: string;
  keywords: string[];
  intro: string;
  useCases: string[];
  howToSteps: ToolStep[];
  offlineMode: ToolOfflineMode;
  syncOnReconnect: boolean;
}

function createToolDefinition(
  tool: Omit<ToolDefinition, 'slug' | 'metadataTitle' | 'metadataDescription' | 'offlineMode' | 'syncOnReconnect'> & Partial<Pick<ToolDefinition, 'offlineMode' | 'syncOnReconnect'>>,
): ToolDefinition {
  return {
    ...tool,
    slug: tool.id,
    metadataTitle: `${tool.name} | Free Online ${tool.category} Tool | 2xora`,
    metadataDescription: tool.description,
    offlineMode: tool.offlineMode ?? 'ready',
    syncOnReconnect: tool.syncOnReconnect ?? true,
  };
}

export const toolDefinitions: ToolDefinition[] = [
  createToolDefinition({
    id: 'qr-code-maker',
    name: 'QR Code Maker',
    description: 'Generate custom QR codes for URLs, text, email, WiFi, and WhatsApp with style options.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'QrCodeIcon',
    gradient: 'from-violet-500 to-purple-600',
    isNew: false,
    badge: null,
    keywords: ['qr code generator', 'free qr code maker', 'wifi qr code', 'url qr code'],
    intro: 'Create branded QR codes with live preview, format flexibility, and quick downloads for print or digital use.',
    useCases: ['Share website links', 'Create event or menu QR codes', 'Generate WiFi and contact QR codes'],
    howToSteps: [
      { title: 'Choose QR content', description: 'Enter the URL, text, email, WiFi details, or message you want the QR code to store.' },
      { title: 'Customize the design', description: 'Adjust colors, shape, spacing, and styling until the QR code matches your brand or campaign.' },
      { title: 'Preview and export', description: 'Scan the preview to confirm it works, then download the final QR code for web, print, or sharing.' },
    ],
  }),
  createToolDefinition({
    id: 'invoice-maker',
    name: 'Invoice Maker',
    description: 'Create polished invoices with templates, line items, tax totals, and export to PDF or image.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'ReceiptPercentIcon',
    gradient: 'from-blue-500 to-cyan-600',
    isNew: true,
    badge: 'New',
    keywords: ['invoice maker', 'invoice generator', 'freelance invoice tool', 'pdf invoice template'],
    intro: 'Build client-ready invoices in the browser with cleaner templates, editable line items, and fast export options.',
    useCases: ['Create invoices for freelance work', 'Export branded client billing documents', 'Prepare polished estimates and payment requests'],
    howToSteps: [
      { title: 'Choose an invoice template', description: 'Start with a template style that matches your brand or the client tone you want to present.' },
      { title: 'Fill in invoice details', description: 'Add your business information, client details, line items, totals, notes, and optional branding.' },
      { title: 'Export and send', description: 'Download the invoice as a PDF or image and send it to your client with a clean professional finish.' },
    ],
  }),
  createToolDefinition({
    id: 'cv-maker',
    name: 'CV Maker',
    description: 'Create polished CVs and resumes with premium layouts, live preview, and export to PDF or image.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'IdentificationIcon',
    gradient: 'from-sky-500 to-indigo-600',
    isNew: true,
    badge: 'New',
    keywords: ['cv maker', 'resume builder', 'resume maker', 'cv template'],
    intro: 'Build a clean, professional CV in the browser with structured sections, premium styling, and fast export options.',
    useCases: ['Create a fresh resume', 'Export a polished CV for applications', 'Keep a browser-saved resume draft'],
    howToSteps: [
      { title: 'Choose a resume style', description: 'Pick a tone and layout that matches the role and level you want to present.' },
      { title: 'Fill in your experience', description: 'Add your summary, skills, work history, education, and contact details.' },
      { title: 'Preview and export', description: 'Review the live CV and export it as a PDF or image when it feels ready.' },
    ],
  }),
  createToolDefinition({
    id: 'social-media',
    name: 'Social Media Tools',
    description: 'Create styled social media icons for 15+ platforms and generate shareable profile links.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'ShareIcon',
    gradient: 'from-pink-500 to-rose-500',
    isNew: false,
    badge: null,
    keywords: ['social media icon generator', 'social links tool', 'social media assets'],
    intro: 'Build social-ready icons and profile links that are easy to reuse across websites, bios, and landing pages.',
    useCases: ['Make icon packs for websites', 'Create profile link blocks', 'Prepare assets for creator pages'],
    howToSteps: [
      { title: 'Select your platforms', description: 'Choose the social networks or profile links you want to include in the final set.' },
      { title: 'Style the output', description: 'Set colors, size, spacing, and visual treatment so the assets fit your brand.' },
      { title: 'Copy or download', description: 'Export the icons or generated links and place them into your site, profile, or campaign.' },
    ],
  }),
  createToolDefinition({
    id: 'table-creator',
    name: 'Table Creator',
    description: 'Build responsive HTML or Markdown tables visually. Copy code or download as CSV.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'TableCellsIcon',
    gradient: 'from-teal-500 to-cyan-600',
    isNew: false,
    badge: null,
    keywords: ['table generator', 'markdown table creator', 'html table builder'],
    intro: 'Create cleaner data tables faster with visual controls and ready-to-copy output for docs, emails, and web pages.',
    useCases: ['Write Markdown tables', 'Create pricing or feature grids', 'Export structured tabular data'],
    howToSteps: [
      { title: 'Add rows and columns', description: 'Enter your headings and table content or paste data into the builder.' },
      { title: 'Format the layout', description: 'Adjust alignment, spacing, responsiveness, and output format for the destination you need.' },
      { title: 'Export the table', description: 'Copy HTML or Markdown code, or download the table data in a reusable format.' },
    ],
  }),
  createToolDefinition({
    id: 'element-creator',
    name: 'Element Creator',
    description: 'Design buttons, badges, cards, and UI elements visually. Export as HTML/CSS or Tailwind.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'CubeIcon',
    gradient: 'from-indigo-500 to-violet-600',
    isNew: false,
    badge: null,
    keywords: ['ui element generator', 'button builder', 'card generator'],
    intro: 'Prototype interface pieces quickly and export production-friendly markup without starting from scratch.',
    useCases: ['Generate CTA buttons', 'Mock up card layouts', 'Build reusable UI snippets'],
    howToSteps: [
      { title: 'Pick an element type', description: 'Start with the kind of UI component you want to build, such as a button, badge, or card.' },
      { title: 'Tune the styling', description: 'Adjust labels, spacing, colors, radius, and other visual controls to match your interface.' },
      { title: 'Export the code', description: 'Copy the generated HTML, CSS, or Tailwind output and drop it into your project.' },
    ],
  }),
  createToolDefinition({
    id: 'meta-tag-generator',
    name: 'Meta Tag Generator',
    description: 'Fill a form to get ready-to-paste Open Graph, Twitter Card, and SEO meta tags.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'CodeBracketIcon',
    gradient: 'from-sky-500 to-blue-600',
    isNew: true,
    badge: 'New',
    keywords: ['meta tag generator', 'open graph generator', 'twitter card tags', 'seo meta tags'],
    intro: 'Generate search and social metadata faster so each page is easier to understand, index, and share.',
    useCases: ['Create page metadata', 'Improve social sharing cards', 'Prepare SEO tags for new landing pages'],
    howToSteps: [
      { title: 'Enter page details', description: 'Add the page title, description, canonical URL, image, and social metadata fields.' },
      { title: 'Review the tag output', description: 'Check the generated Open Graph, Twitter Card, and standard SEO tags for accuracy.' },
      { title: 'Paste into your site', description: 'Copy the metadata block into your page head, layout, or framework metadata config.' },
    ],
  }),
  createToolDefinition({
    id: 'placeholder-image',
    name: 'Placeholder Image',
    description: 'Generate placeholder images with custom size, color, and text. Download as SVG or PNG.',
    category: 'Generators',
    categoryColor: 'violet',
    icon: 'PhotoIcon',
    gradient: 'from-lime-500 to-green-600',
    isNew: true,
    badge: 'New',
    keywords: ['placeholder image generator', 'svg placeholder', 'mock image'],
    intro: 'Create quick stand-in graphics for mockups, layouts, demos, and early-stage product builds.',
    useCases: ['Prototype image slots', 'Share wireframes with content placeholders', 'Build test assets for layouts'],
    howToSteps: [
      { title: 'Set the dimensions', description: 'Choose the exact width, height, and placeholder text you want to display.' },
      { title: 'Adjust the visual style', description: 'Customize colors, typography, and format so the placeholder fits your mockup.' },
      { title: 'Download the asset', description: 'Export the placeholder image and use it in prototypes, staging pages, or demos.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-to-word',
    name: 'PDF to Word',
    description: 'Convert PDF documents to editable Word (.docx) files while preserving formatting.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'DocumentArrowDownIcon',
    gradient: 'from-red-500 to-rose-600',
    isNew: false,
    badge: null,
    keywords: ['pdf to word', 'convert pdf to docx', 'editable pdf converter'],
    intro: 'Turn PDF documents into editable files for revisions, content reuse, and internal collaboration.',
    useCases: ['Edit contract drafts', 'Reuse report content', 'Convert archived PDFs into working documents'],
    offlineMode: 'online-required',
    howToSteps: [
      { title: 'Upload your PDF', description: 'Select the PDF file you want to convert into an editable Word document.' },
      { title: 'Start the conversion', description: 'Run the tool and let it process the document while preserving readable structure.' },
      { title: 'Download the Word file', description: 'Open the exported document in Word or Google Docs and continue editing from there.' },
    ],
  }),
  createToolDefinition({
    id: 'word-to-pdf',
    name: 'Word to PDF',
    description: 'Convert Word documents (.docx, .doc) to PDF with perfect layout preservation.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'DocumentArrowUpIcon',
    gradient: 'from-orange-500 to-red-500',
    isNew: false,
    badge: null,
    keywords: ['word to pdf', 'docx to pdf', 'document to pdf converter'],
    intro: 'Export polished PDFs from editable documents for sharing, delivery, and print-ready handoff.',
    useCases: ['Send resumes as PDF', 'Export client documents', 'Preserve document layout for sharing'],
    offlineMode: 'online-required',
    howToSteps: [
      { title: 'Choose a Word file', description: 'Upload the .doc or .docx document that you want to convert into PDF.' },
      { title: 'Confirm the output', description: 'Review the conversion settings or preview so the file matches your expected layout.' },
      { title: 'Download the PDF', description: 'Save the generated PDF and share it anywhere a fixed-layout document is needed.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-unlocker',
    name: 'PDF Unlocker',
    description: 'Remove password protection from PDF files you own. Unlocks in seconds.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'LockOpenIcon',
    gradient: 'from-yellow-500 to-amber-600',
    isNew: false,
    badge: null,
    keywords: ['pdf unlocker', 'remove pdf password', 'unlock protected pdf'],
    intro: 'Regain access to your own protected PDF files so they are easier to read, edit, or archive.',
    useCases: ['Open personal files faster', 'Remove repeat password prompts', 'Prepare PDFs for internal workflows'],
    howToSteps: [
      { title: 'Upload the protected PDF', description: 'Choose the PDF file you own and provide the current password if required.' },
      { title: 'Unlock the document', description: 'Run the unlock process to remove restrictions from the accessible copy.' },
      { title: 'Save the new version', description: 'Download the unlocked PDF and store it securely for future access.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-locker',
    name: 'PDF Locker',
    description: 'Password-protect your PDF files with AES-256 encryption. Control printing, copying, and editing permissions.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'LockClosedIcon',
    gradient: 'from-rose-500 to-red-600',
    isNew: false,
    badge: null,
    keywords: ['pdf locker', 'protect pdf with password', 'secure pdf'],
    intro: 'Add practical security to PDFs before sending them to clients, teammates, or external partners.',
    useCases: ['Protect invoices and contracts', 'Secure internal reports', 'Limit editing or printing access'],
    howToSteps: [
      { title: 'Upload the PDF', description: 'Select the file you want to protect before sharing or storing it.' },
      { title: 'Set protection rules', description: 'Add a password and configure the permissions you want to apply to the document.' },
      { title: 'Download the secured file', description: 'Export the locked PDF and distribute it only to the right recipients.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-to-image',
    name: 'PDF to Image',
    description: 'Convert PDF pages into high-quality PNG, JPG, or WebP images and download them one by one or all at once.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'PhotoIcon',
    gradient: 'from-fuchsia-500 to-pink-600',
    isNew: true,
    badge: 'New',
    keywords: ['pdf to image', 'pdf to png', 'pdf to jpg', 'pdf pages to images'],
    intro: 'Turn each PDF page into a crisp image for sharing, design work, previews, or visual archives without sacrificing clarity.',
    useCases: ['Extract PDF pages as images', 'Prepare pages for social sharing', 'Reuse report pages in design workflows'],
    howToSteps: [
      { title: 'Upload the PDF', description: 'Choose the PDF you want to turn into separate image pages.' },
      { title: 'Render the pages', description: 'Load the document, preview every page, and choose the image format and quality you want.' },
      { title: 'Download one or all', description: 'Save individual pages or export the whole document as a ZIP of images.' },
    ],
  }),
  createToolDefinition({
    id: 'image-to-pdf',
    name: 'Image to PDF',
    description: 'Combine JPG, PNG, and WebP images into a polished PDF with the same order you upload.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'PhotoIcon',
    gradient: 'from-fuchsia-500 to-rose-600',
    isNew: true,
    badge: 'New',
    keywords: ['image to pdf', 'jpg to pdf', 'png to pdf', 'combine images pdf'],
    intro: 'Turn screenshots, scans, and photos into one shareable PDF without leaving the browser.',
    useCases: ['Bundle receipts into one PDF', 'Turn scans into a document set', 'Export visual moodboards as PDF'],
    howToSteps: [
      { title: 'Upload your images', description: 'Add the JPG, PNG, or WebP files you want to combine into a single document.' },
      { title: 'Keep the order you need', description: 'Review the file order so the pages appear exactly how you want them in the final PDF.' },
      { title: 'Download the PDF', description: 'Export the combined document and share it as a clean, portable PDF file.' },
    ],
  }),
  createToolDefinition({
    id: 'sign-pdf',
    name: 'Signature on PDF',
    description: 'Upload a PDF, place your handwritten signature, and export a signed copy in seconds.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'PencilSquareIcon',
    gradient: 'from-emerald-500 to-teal-600',
    isNew: true,
    badge: 'New',
    keywords: ['sign pdf', 'pdf signature', 'add signature to pdf'],
    intro: 'Sign approval sheets, forms, and documents directly in the browser without printing and rescanning.',
    useCases: ['Sign client approvals', 'Sign contracts fast', 'Approve internal PDF documents'],
    howToSteps: [
      { title: 'Upload the PDF', description: 'Open the file you want to sign, including protected files you have permission to access.' },
      { title: 'Draw and place your signature', description: 'Create your signature, position it on the right page, and preview the result.' },
      { title: 'Download the signed copy', description: 'Export the updated PDF with your signature applied.' },
    ],
  }),
  createToolDefinition({
    id: 'sign-image',
    name: 'Signature on Picture',
    description: 'Add a handwritten signature to screenshots, scanned forms, or photos and export the signed image.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'PhotoIcon',
    gradient: 'from-sky-500 to-blue-600',
    isNew: true,
    badge: 'New',
    keywords: ['sign image', 'signature on photo', 'add signature to picture'],
    intro: 'Sign image-based paperwork or marked-up visuals quickly when the source is not already a PDF.',
    useCases: ['Sign scanned forms', 'Approve screenshots', 'Add signatures to photo-based documents'],
    howToSteps: [
      { title: 'Upload your image', description: 'Choose the photo, scan, or screenshot you want to sign.' },
      { title: 'Draw the signature', description: 'Use the signature pad and place it exactly where it should appear on the image.' },
      { title: 'Download the signed file', description: 'Export the updated image and share it immediately.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-editor',
    name: 'PDF Editor',
    description: 'Upload a PDF, add notes or a signature, and export an edited version directly in the browser.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'DocumentTextIcon',
    gradient: 'from-indigo-500 to-blue-600',
    isNew: true,
    badge: 'New',
    keywords: ['pdf editor', 'edit pdf online', 'annotate pdf'],
    intro: 'Make quick edits to PDFs by adding text and signatures without opening a heavyweight desktop editor.',
    useCases: ['Annotate review PDFs', 'Add approval notes', 'Prepare marked-up PDFs for clients'],
    howToSteps: [
      { title: 'Upload the PDF', description: 'Open the PDF you want to edit and load the pages into the browser editor.' },
      { title: 'Add text or signature', description: 'Place your note, annotation, or signature on the page you choose.' },
      { title: 'Export the new file', description: 'Download the edited PDF once the placement looks right.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-split',
    name: 'PDF Split',
    description: 'Split a PDF by page range and download only the pages you need.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'ScissorsIcon',
    gradient: 'from-rose-500 to-orange-500',
    isNew: true,
    badge: 'New',
    keywords: ['pdf split', 'split pdf pages', 'extract pdf pages'],
    intro: 'Break large PDFs into smaller sections quickly when you only need a few pages.',
    useCases: ['Extract contract pages', 'Send only the needed pages', 'Break reports into smaller files'],
    howToSteps: [
      { title: 'Upload your PDF', description: 'Choose the document you want to break into a smaller file.' },
      { title: 'Enter the page ranges', description: 'Use values like 1-3,5,8 to pick the exact pages to keep.' },
      { title: 'Download the split file', description: 'Export the new PDF that contains only the selected pages.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-to-powerpoint',
    name: 'PDF to PowerPoint',
    description: 'Turn PDF pages into PowerPoint slides with readable notes.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'PresentationChartBarIcon',
    gradient: 'from-orange-500 to-amber-500',
    isNew: true,
    badge: 'New',
    keywords: ['pdf to powerpoint', 'pdf to ppt', 'pdf slides'],
    intro: 'Turn a PDF deck into slides that are easier to present, reorder, and remix.',
    useCases: ['Convert pitch decks', 'Reuse report pages in slides', 'Turn handouts into presentations'],
    offlineMode: 'online-required',
    howToSteps: [
      { title: 'Upload the PDF', description: 'Select the PDF you want to turn into a PowerPoint deck.' },
      { title: 'Run the conversion', description: 'Create a slide for each page while keeping readable notes attached.' },
      { title: 'Download the PowerPoint', description: 'Open the exported deck in PowerPoint or Google Slides and keep editing.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-to-excel',
    name: 'PDF to Excel',
    description: 'Extract readable PDF text into an Excel workbook with separate page sheets.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'TableCellsIcon',
    gradient: 'from-emerald-500 to-green-600',
    isNew: true,
    badge: 'New',
    keywords: ['pdf to excel', 'pdf to xlsx', 'extract pdf text to excel'],
    intro: 'Move readable PDF content into a spreadsheet so it is easier to sort, share, and analyze.',
    useCases: ['Pull text into Excel', 'Review page content in sheets', 'Organize extracted report text'],
    offlineMode: 'online-required',
    howToSteps: [
      { title: 'Upload the PDF', description: 'Choose the file you want to extract text from.' },
      { title: 'Create the workbook', description: 'Generate a workbook with a summary sheet and page-by-page text sheets.' },
      { title: 'Open in Excel', description: 'Download the XLSX file and continue cleaning or analyzing the data.' },
    ],
  }),
  createToolDefinition({
    id: 'powerpoint-to-pdf',
    name: 'PowerPoint to PDF',
    description: 'Convert a PowerPoint deck into a readable PDF draft directly in the browser.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'PresentationChartBarIcon',
    gradient: 'from-red-500 to-orange-600',
    isNew: true,
    badge: 'New',
    keywords: ['powerpoint to pdf', 'ppt to pdf', 'pptx to pdf'],
    intro: 'Turn a PowerPoint deck into a simple PDF draft when you need something easy to share fast.',
    useCases: ['Share slide drafts', 'Archive presentation text', 'Review deck content as PDF'],
    offlineMode: 'online-required',
    howToSteps: [
      { title: 'Upload the deck', description: 'Choose the .pptx or .ppt file you want to convert.' },
      { title: 'Create the PDF', description: 'Extract the slide content and rebuild it into a readable PDF draft.' },
      { title: 'Download the result', description: 'Save the generated PDF and send it wherever a PDF is easier to review.' },
    ],
  }),
  createToolDefinition({
    id: 'watermark-maker',
    name: 'Watermark Maker',
    description: 'Apply a text or image watermark to a PDF or picture in one place.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'ShieldCheckIcon',
    gradient: 'from-slate-700 to-slate-900',
    isNew: true,
    badge: 'New',
    keywords: ['watermark pdf', 'watermark image', 'add watermark'],
    intro: 'Protect drafts, proofs, and branded assets with a quick text or logo watermark.',
    useCases: ['Mark confidential PDFs', 'Brand proof images', 'Protect previews before sharing'],
    howToSteps: [
      { title: 'Upload a PDF or picture', description: 'Choose the document or image that needs the watermark.' },
      { title: 'Choose text or image watermark', description: 'Use text, draw a quick mark, or upload a logo for the overlay.' },
      { title: 'Export the watermarked file', description: 'Download the updated file with the watermark applied.' },
    ],
  }),
  createToolDefinition({
    id: 'html-to-pdf',
    name: 'HTML to PDF',
    description: 'Paste HTML and export a rendered PDF snapshot from the browser.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'CodeBracketIcon',
    gradient: 'from-sky-500 to-indigo-600',
    isNew: true,
    badge: 'New',
    keywords: ['html to pdf', 'convert html to pdf', 'pdf from markup'],
    intro: 'Turn snippets of HTML into quick PDF exports without setting up a separate rendering pipeline.',
    useCases: ['Export invoice markup', 'Capture landing page snippets', 'Share HTML previews as PDF'],
    howToSteps: [
      { title: 'Paste your HTML', description: 'Add the markup you want to render into the PDF preview.' },
      { title: 'Review the browser preview', description: 'Check how the rendered content looks before you export it.' },
      { title: 'Download the PDF', description: 'Generate the PDF snapshot and save it for sharing.' },
    ],
  }),
  createToolDefinition({
    id: 'pdf-organiser',
    name: 'PDF Organiser',
    description: 'Reorder PDF pages, disable the extras, and export a cleaner file.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'Squares2X2Icon',
    gradient: 'from-indigo-500 to-violet-600',
    isNew: true,
    badge: 'New',
    keywords: ['pdf organiser', 'reorder pdf pages', 'remove pdf pages'],
    intro: 'Clean up PDFs by changing page order and keeping only the parts that matter.',
    useCases: ['Reorder presentations', 'Remove extra appendix pages', 'Prepare final delivery files'],
    howToSteps: [
      { title: 'Load the PDF pages', description: 'Open the document and bring its pages into the organizer grid.' },
      { title: 'Move or disable pages', description: 'Shift pages up or down and uncheck the ones you want to drop.' },
      { title: 'Export the organised PDF', description: 'Download a new PDF with the final sequence you chose.' },
    ],
  }),
  createToolDefinition({
    id: 'repair-pdf',
    name: 'Repair PDF',
    description: 'Rebuild a PDF into a fresh copy when the original file is unstable or unreadable.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'WrenchScrewdriverIcon',
    gradient: 'from-amber-500 to-orange-600',
    isNew: true,
    badge: 'New',
    keywords: ['repair pdf', 'fix pdf', 'rebuild pdf'],
    intro: 'Refresh glitchy PDFs by reconstructing them into a clean browser-generated copy.',
    useCases: ['Fix broken previews', 'Resave unstable PDFs', 'Rebuild files before sending again'],
    offlineMode: 'online-required',
    howToSteps: [
      { title: 'Upload the PDF', description: 'Choose the file that needs a fresh rebuilt copy.' },
      { title: 'Run the repair', description: 'Open and rebuild the document page by page in the browser.' },
      { title: 'Download the repaired PDF', description: 'Save the new PDF and use it as the cleaner working copy.' },
    ],
  }),
  createToolDefinition({
    id: 'crop-pdf',
    name: 'Crop PDF',
    description: 'Trim page edges from a PDF and export the cropped version.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'ArrowsPointingInIcon',
    gradient: 'from-pink-500 to-rose-600',
    isNew: true,
    badge: 'New',
    keywords: ['crop pdf', 'trim pdf margins', 'pdf crop tool'],
    intro: 'Remove unwanted margins and focus the page on the content you care about most.',
    useCases: ['Trim scan borders', 'Crop wide margins', 'Tighten exported proofs'],
    howToSteps: [
      { title: 'Load the PDF', description: 'Upload your document and preview the pages in the crop tool.' },
      { title: 'Adjust the crop margins', description: 'Set how much to trim from each edge until the preview looks right.' },
      { title: 'Download the cropped file', description: 'Export a new PDF with the tighter page framing applied.' },
    ],
  }),
  createToolDefinition({
    id: 'document-merger',
    name: 'Document Merger',
    description: 'Merge PDFs and image files into one ordered PDF document right in the browser.',
    category: 'PDF',
    categoryColor: 'coral',
    icon: 'DocumentDuplicateIcon',
    gradient: 'from-rose-500 to-orange-500',
    isNew: true,
    badge: 'New',
    keywords: ['document merger', 'merge pdf and images', 'combine documents', 'merge files to pdf'],
    intro: 'Build one shareable PDF from scattered PDFs and images without a desktop app or upload-heavy workflow.',
    useCases: ['Combine signed pages and scans', 'Merge receipts into one document', 'Create one client-ready PDF pack'],
    howToSteps: [
      { title: 'Upload the files', description: 'Add the PDFs and images you want to combine into one output document.' },
      { title: 'Arrange the order', description: 'Move files up or down until the final document stack is in the right sequence.' },
      { title: 'Download the merged PDF', description: 'Export the finished combined document as one clean PDF file.' },
    ],
  }),
  createToolDefinition({
    id: 'background-remover',
    name: 'Background Remover',
    description: 'Auto-remove image backgrounds instantly. Download as PNG, WebP, or SVG.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'ScissorsIcon',
    gradient: 'from-emerald-500 to-teal-600',
    isNew: false,
    badge: null,
    keywords: ['background remover', 'remove image background', 'transparent png maker'],
    intro: 'Cut out product shots, portraits, and marketing visuals faster without needing a heavy design app.',
    useCases: ['Prepare ecommerce images', 'Make transparent PNG assets', 'Create clean cutouts for social posts'],
    howToSteps: [
      { title: 'Upload your image', description: 'Start with the photo or graphic that needs its background removed.' },
      { title: 'Refine the cutout', description: 'Use the automatic result and any cleanup controls to improve edges or preserve details.' },
      { title: 'Export the transparent file', description: 'Download the final image in the format that works best for your next use.' },
    ],
  }),
  createToolDefinition({
    id: 'image-converter',
    name: 'Image Converter',
    description: 'Convert between PNG, JPG, WebP, AVIF, GIF, and SVG vector format in bulk.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'ArrowsRightLeftIcon',
    gradient: 'from-cyan-500 to-blue-600',
    isNew: false,
    badge: null,
    keywords: ['image converter', 'png to jpg', 'webp converter', 'avif converter'],
    intro: 'Switch image formats quickly to improve compatibility, performance, or design handoff requirements.',
    useCases: ['Convert assets for the web', 'Prepare images for upload limits', 'Standardize mixed image libraries'],
    howToSteps: [
      { title: 'Add the source files', description: 'Upload one or more images that need to be converted to another format.' },
      { title: 'Choose the target format', description: 'Select the output type and adjust quality or other available export options.' },
      { title: 'Download the converted images', description: 'Save the finished files and replace the originals wherever you need them.' },
    ],
  }),
  createToolDefinition({
    id: 'heic-to-jpg-png',
    name: 'HEIC to JPG/PNG',
    description: 'Convert HEIC or HEIF photos into JPG or PNG when your browser can decode the source image.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'ArrowsRightLeftIcon',
    gradient: 'from-sky-500 to-blue-600',
    isNew: true,
    badge: 'New',
    keywords: ['heic to jpg', 'heic to png', 'iphone photo converter', 'heif converter'],
    intro: 'Turn iPhone-style HEIC photos into easier JPG and PNG files for sharing, editing, and uploads.',
    useCases: ['Convert iPhone camera photos', 'Prepare HEIC files for websites', 'Open HEIC images in broader apps'],
    howToSteps: [
      { title: 'Upload the HEIC files', description: 'Add the HEIC or HEIF photos you want to convert into wider-format image files.' },
      { title: 'Choose JPG or PNG', description: 'Pick the output format that fits your next use, then run the conversion.' },
      { title: 'Download the exports', description: 'Save the converted images and use them anywhere JPG or PNG is easier to handle.' },
    ],
  }),
  createToolDefinition({
    id: 'webp-to-png-jpg',
    name: 'WebP to PNG/JPG',
    description: 'Turn WebP images into PNG or JPG output for broader app, upload, and editing compatibility.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'PhotoIcon',
    gradient: 'from-cyan-500 to-sky-600',
    isNew: true,
    badge: 'New',
    keywords: ['webp to png', 'webp to jpg', 'convert webp image', 'webp converter'],
    intro: 'Convert WebP graphics into broader image formats whenever uploads or software need PNG or JPG instead.',
    useCases: ['Prepare web graphics for clients', 'Upload to systems that reject WebP', 'Edit WebP images in older apps'],
    howToSteps: [
      { title: 'Upload the WebP images', description: 'Select one or more WebP files you want to convert.' },
      { title: 'Pick PNG or JPG', description: 'Set the output format and quality for the new export.' },
      { title: 'Download the finished files', description: 'Save each converted image or download the full set after conversion.' },
    ],
  }),
  createToolDefinition({
    id: 'image-resizer',
    name: 'Image Resizer',
    description: 'Resize images to exact dimensions, keep aspect ratio, and choose contain, cover, or stretch output.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'ArrowsPointingOutIcon',
    gradient: 'from-sky-500 to-cyan-600',
    isNew: true,
    badge: 'New',
    keywords: ['image resizer', 'resize image online', 'change image dimensions', 'photo resizer'],
    intro: 'Resize screenshots, product images, and assets with exact width and height controls right in the browser.',
    useCases: ['Prepare social media sizes', 'Resize website assets', 'Fit uploads to exact dimensions'],
    howToSteps: [
      { title: 'Upload your image', description: 'Start with the picture or asset that needs new dimensions.' },
      { title: 'Set the target size', description: 'Enter width and height, lock the ratio if needed, and choose contain, cover, or stretch.' },
      { title: 'Export the resized version', description: 'Download the finished file in PNG, JPG, or WebP once the preview looks right.' },
    ],
  }),
  createToolDefinition({
    id: 'image-cropper',
    name: 'Image Cropper',
    description: 'Drag a crop box over an image, move it around, and export a cleaner cropped version fast.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'ArrowsPointingInIcon',
    gradient: 'from-blue-500 to-cyan-600',
    isNew: true,
    badge: 'New',
    keywords: ['image cropper', 'crop photo', 'crop image online', 'photo crop tool'],
    intro: 'Crop screenshots, portraits, and assets with a direct drag-and-export workflow that stays entirely in the browser.',
    useCases: ['Trim distractions from a photo', 'Prepare tighter image crops', 'Create cleaner hero or product shots'],
    howToSteps: [
      { title: 'Upload the image', description: 'Open the image you want to crop and preview inside the crop editor.' },
      { title: 'Draw or move the crop', description: 'Drag a new crop area or reposition the current one until the framing feels right.' },
      { title: 'Export the result', description: 'Download the cropped image in the output format that fits your workflow.' },
    ],
  }),
  createToolDefinition({
    id: 'image-compressor',
    name: 'Image Compressor',
    description: 'Compress PNG, JPG, and WebP images in-browser with quality slider. Batch supported.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'ArrowDownTrayIcon',
    gradient: 'from-teal-500 to-emerald-600',
    isNew: true,
    badge: 'New',
    keywords: ['image compressor', 'reduce image size', 'compress jpg png webp'],
    intro: 'Shrink image file sizes for faster pages, cleaner uploads, and better performance without extra software.',
    useCases: ['Optimize website assets', 'Reduce upload size', 'Compress batches of screenshots or photos'],
    howToSteps: [
      { title: 'Upload the images', description: 'Choose the PNG, JPG, or WebP files you want to make smaller.' },
      { title: 'Adjust compression', description: 'Move the quality settings until the preview balances size and visual clarity.' },
      { title: 'Export the optimized files', description: 'Download the compressed images and use them on your site or in your workflow.' },
    ],
  }),
  createToolDefinition({
    id: 'bulk-image-rename',
    name: 'Bulk Image Rename',
    description: 'Rename multiple images at once with prefixes, suffixes, numbering, and ZIP download.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'QueueListIcon',
    gradient: 'from-emerald-500 to-cyan-600',
    isNew: true,
    badge: 'New',
    keywords: ['bulk image rename', 'rename images', 'rename files in batch', 'image zip download'],
    intro: 'Clean up messy image filenames in one pass so client handoff, asset management, and uploads stay more organized.',
    useCases: ['Rename product photos in bulk', 'Prepare clean client asset names', 'Standardize image libraries fast'],
    howToSteps: [
      { title: 'Upload your images', description: 'Add the image files that need a cleaner naming pattern.' },
      { title: 'Set the rename pattern', description: 'Choose a prefix, suffix, starting number, and padding until the preview looks right.' },
      { title: 'Download the ZIP', description: 'Export the renamed files together as one ZIP package for easier delivery.' },
    ],
  }),
  createToolDefinition({
    id: 'favicon-generator',
    name: 'Favicon Generator',
    description: 'Create favicons from text or image in all sizes (16x16 to 512x512). Download as PNG.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'StarIcon',
    gradient: 'from-amber-500 to-orange-600',
    isNew: true,
    badge: 'New',
    keywords: ['favicon generator', 'app icon maker', 'website favicon'],
    intro: 'Generate browser-ready icons faster for websites, dashboards, and side projects.',
    useCases: ['Create a site favicon', 'Generate app icon sizes', 'Test icon ideas from text or logos'],
    howToSteps: [
      { title: 'Choose your source', description: 'Start with text, initials, or an uploaded image that should become the favicon.' },
      { title: 'Customize the design', description: 'Set colors, shape, padding, and sizing so the icon stays clear at small dimensions.' },
      { title: 'Download the favicon files', description: 'Export the generated icon sizes and add them to your site or app.' },
    ],
  }),
  createToolDefinition({
    id: 'id-photo-maker',
    name: 'ID & Passport Photo Maker',
    description: 'Prepare passport and ID photo crops with ready-made output sizes and clean export settings.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'IdentificationIcon',
    gradient: 'from-indigo-500 to-sky-600',
    isNew: true,
    badge: 'New',
    keywords: ['passport photo maker', 'id photo tool', 'visa photo cropper', 'passport image size'],
    intro: 'Make cleaner ID and passport photo crops with the right framing and export sizes before printing or submission.',
    useCases: ['Prepare passport photos', 'Create staff ID images', 'Make visa-ready headshots'],
    howToSteps: [
      { title: 'Upload the portrait', description: 'Start with the portrait photo that needs to fit an ID or passport layout.' },
      { title: 'Choose the photo preset', description: 'Pick the target format and adjust the crop until the framing works.' },
      { title: 'Export the final photo', description: 'Download the correctly sized image for print, upload, or application use.' },
    ],
  }),
  createToolDefinition({
    id: 'profile-picture-maker',
    name: 'Profile Picture Maker',
    description: 'Crop and export polished avatars for social, work, and creator profiles with ready-made presets.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'UserCircleIcon',
    gradient: 'from-violet-500 to-fuchsia-600',
    isNew: true,
    badge: 'New',
    keywords: ['profile picture maker', 'avatar cropper', 'social profile image', 'headshot crop tool'],
    intro: 'Build cleaner profile images for platforms, teams, and communities without jumping into a design app.',
    useCases: ['Prepare social profile photos', 'Create avatar crops for communities', 'Export polished team profile images'],
    howToSteps: [
      { title: 'Upload the portrait', description: 'Open the headshot or image you want to turn into a profile-ready picture.' },
      { title: 'Pick the profile preset', description: 'Choose a preset and refine the crop until the subject sits perfectly.' },
      { title: 'Download the avatar', description: 'Export the finished profile picture in the size and format you need.' },
    ],
  }),
  createToolDefinition({
    id: 'color-picker',
    name: 'Color Picker',
    description: 'Sample colors directly from any uploaded image and build a quick palette with copied hex values.',
    category: 'Image',
    categoryColor: 'cyan',
    icon: 'EyeDropperIcon',
    gradient: 'from-amber-500 to-pink-500',
    isNew: true,
    badge: 'New',
    keywords: ['color picker', 'pick colors from image', 'hex color extractor', 'image palette tool'],
    intro: 'Grab exact color values from images faster when you need a palette for design, branding, or frontend work.',
    useCases: ['Extract colors from brand visuals', 'Build palettes from photos', 'Copy exact hex codes from references'],
    howToSteps: [
      { title: 'Upload an image', description: 'Open the screenshot, photo, or asset you want to sample colors from.' },
      { title: 'Tap to sample colors', description: 'Click anywhere on the preview to collect the exact pixel color.' },
      { title: 'Copy the palette', description: 'Reuse the generated hex values in design tools, CSS, or brand systems.' },
    ],
  }),
  createToolDefinition({
    id: '3d-icon-maker',
    name: '3D Icon Maker',
    description: 'Create stunning 3D-style icons with clay, glass, neon, and metallic effects. Export as SVG, PNG, or WebP.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'CubeTransparentIcon',
    gradient: 'from-violet-500 to-indigo-600',
    isNew: false,
    badge: null,
    keywords: ['3d icon maker', 'icon generator', 'app icon design'],
    intro: 'Design expressive icons for marketing pages, product surfaces, and presentations with faster visual iteration.',
    useCases: ['Create hero illustrations', 'Make product feature icons', 'Design app marketing assets'],
    howToSteps: [
      { title: 'Select an icon concept', description: 'Choose the icon style, subject, or base direction you want to develop.' },
      { title: 'Apply 3D styling', description: 'Adjust effects such as lighting, material, depth, and color treatment.' },
      { title: 'Export the final icon', description: 'Download the icon in the format that best fits your design or dev workflow.' },
    ],
  }),
  createToolDefinition({
    id: 'color-palette',
    name: 'Color Palette Creator',
    description: 'Enter primary and secondary brand colors to generate a full 8-color design system palette.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'SwatchIcon',
    gradient: 'from-fuchsia-500 to-pink-600',
    isNew: false,
    badge: null,
    keywords: ['color palette generator', 'brand palette creator', 'design system colors'],
    intro: 'Build more consistent brand colors and supporting shades for interfaces, graphics, and system tokens.',
    useCases: ['Create brand palettes', 'Generate UI color scales', 'Prepare design token starting points'],
    howToSteps: [
      { title: 'Add your base colors', description: 'Enter the primary and secondary colors that anchor your visual system.' },
      { title: 'Review the generated palette', description: 'Inspect the shades, accents, and pairings created from the selected colors.' },
      { title: 'Copy your colors', description: 'Use the palette values in design tools, CSS variables, or your design system docs.' },
    ],
  }),
  createToolDefinition({
    id: 'gradient-generator',
    name: 'Gradient Generator',
    description: 'Build CSS linear, radial, and conic gradients visually. Export as CSS or SVG.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'PaintBrushIcon',
    gradient: 'from-rose-500 to-orange-500',
    isNew: true,
    badge: 'New',
    keywords: ['gradient generator', 'css gradient builder', 'radial gradient tool'],
    intro: 'Create polished gradients quickly for hero sections, buttons, cards, and backgrounds without manual CSS trial and error.',
    useCases: ['Design landing page backgrounds', 'Create colorful button styles', 'Prototype visual systems'],
    howToSteps: [
      { title: 'Choose a gradient type', description: 'Start with a linear, radial, or conic gradient based on the effect you need.' },
      { title: 'Tune the stops and angle', description: 'Adjust colors, stop positions, and direction until the preview feels right.' },
      { title: 'Copy the output', description: 'Export the generated CSS or SVG and paste it into your project.' },
    ],
  }),
  createToolDefinition({
    id: 'box-shadow-studio',
    name: 'Box Shadow Studio',
    description: 'Layer multiple shadows with full controls. Copy the CSS box-shadow output instantly.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'Square2StackIcon',
    gradient: 'from-slate-500 to-gray-600',
    isNew: true,
    badge: 'New',
    keywords: ['box shadow generator', 'css shadow builder', 'shadow studio'],
    intro: 'Dial in depth and elevation visually before shipping a cleaner shadow system to production.',
    useCases: ['Style cards and modals', 'Build depth systems', 'Fine-tune component shadows'],
    howToSteps: [
      { title: 'Create a shadow stack', description: 'Add one or more shadow layers to build the overall depth effect.' },
      { title: 'Adjust each layer', description: 'Set blur, spread, opacity, color, and offsets while watching the live preview.' },
      { title: 'Copy the CSS', description: 'Export the final box-shadow declaration and apply it to your component styles.' },
    ],
  }),
  createToolDefinition({
    id: 'glassmorphism-generator',
    name: 'Glassmorphism Generator',
    description: 'Create frosted glass UI effects with blur, opacity, and border controls. CSS export.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'ViewfinderCircleIcon',
    gradient: 'from-sky-400 to-blue-500',
    isNew: true,
    badge: 'New',
    keywords: ['glassmorphism generator', 'frosted glass css', 'glass ui'],
    intro: 'Build glass-style interface treatments with faster iteration and easier handoff to engineering.',
    useCases: ['Style dashboards', 'Design floating cards', 'Create frosted overlays'],
    howToSteps: [
      { title: 'Start with a panel', description: 'Choose the glass card or surface area you want to style.' },
      { title: 'Set blur and transparency', description: 'Adjust opacity, border, blur, and shadow values until the effect feels balanced.' },
      { title: 'Export the CSS', description: 'Copy the generated styling and use it on your UI components.' },
    ],
  }),
  createToolDefinition({
    id: 'neumorphism-generator',
    name: 'Neumorphism Generator',
    description: 'Soft UI builder with flat, concave, convex, and pressed shapes. CSS export.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'CircleStackIcon',
    gradient: 'from-gray-400 to-slate-500',
    isNew: true,
    badge: 'New',
    keywords: ['neumorphism generator', 'soft ui generator', 'css neumorphism'],
    intro: 'Design soft, tactile UI treatments with live controls instead of hand-tuning layered shadows.',
    useCases: ['Prototype soft UI elements', 'Create tactile buttons', 'Explore neumorphic card styles'],
    howToSteps: [
      { title: 'Pick a component surface', description: 'Choose the shape or element you want to style with the soft UI effect.' },
      { title: 'Adjust lighting and depth', description: 'Tune shadows, highlights, radius, and pressed or raised states in the preview.' },
      { title: 'Copy the generated CSS', description: 'Export the final styles and reuse them in your app or prototype.' },
    ],
  }),
  createToolDefinition({
    id: 'contrast-checker',
    name: 'Contrast Checker',
    description: 'Check WCAG AA/AAA compliance for foreground and background color pairs.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'AdjustmentsHorizontalIcon',
    gradient: 'from-yellow-500 to-amber-600',
    isNew: true,
    badge: 'New',
    keywords: ['contrast checker', 'wcag contrast tool', 'accessibility color checker'],
    intro: 'Validate color accessibility quickly so text, buttons, and interface elements stay readable for more users.',
    useCases: ['Review design systems', 'Check button accessibility', 'Validate text on brand backgrounds'],
    howToSteps: [
      { title: 'Enter your colors', description: 'Add the foreground and background colors you want to test.' },
      { title: 'Review the contrast score', description: 'Check the ratio and see whether the pair passes WCAG AA or AAA targets.' },
      { title: 'Refine until it passes', description: 'Adjust the colors and test again until the combination is accessible enough.' },
    ],
  }),
  createToolDefinition({
    id: 'css-clip-path',
    name: 'CSS Clip Path Maker',
    description: 'Drag handles on a visual canvas to create custom clip-path shapes. Copy CSS output.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'ScissorsIcon',
    gradient: 'from-purple-500 to-violet-600',
    isNew: true,
    badge: 'New',
    keywords: ['clip path generator', 'css clip-path', 'shape maker'],
    intro: 'Build custom shapes faster for image masks, section dividers, and decorative UI treatments.',
    useCases: ['Create hero masks', 'Build decorative section cuts', 'Prototype custom clipped components'],
    howToSteps: [
      { title: 'Choose a base shape', description: 'Start with a shape preset or drag points manually on the canvas.' },
      { title: 'Fine-tune the geometry', description: 'Move the handles until the clipped area matches the design you need.' },
      { title: 'Copy the clip-path CSS', description: 'Use the generated code directly in your styles or component classes.' },
    ],
  }),
  createToolDefinition({
    id: 'typography-scale',
    name: 'Typography Scale Generator',
    description: 'Pick a base size and scale ratio to get a full type system with CSS variables and Tailwind config.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'DocumentTextIcon',
    gradient: 'from-indigo-500 to-blue-600',
    isNew: true,
    badge: 'New',
    keywords: ['typography scale generator', 'type scale', 'css font scale'],
    intro: 'Create a more intentional type system for websites and products with less manual calculation.',
    useCases: ['Build design tokens', 'Set heading scales', 'Generate Tailwind typography values'],
    howToSteps: [
      { title: 'Set the base type size', description: 'Choose the body size and scale ratio that should drive the full system.' },
      { title: 'Review the scale', description: 'Inspect headings, body sizes, and proportional steps in the generated output.' },
      { title: 'Export the values', description: 'Copy the CSS variables or config values into your project or design system.' },
    ],
  }),
  createToolDefinition({
    id: 'border-radius-generator',
    name: 'Border Radius Generator',
    description: 'Control all 4 corners independently with visual sliders. Copy the CSS output.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'StopIcon',
    gradient: 'from-emerald-500 to-teal-600',
    isNew: true,
    badge: 'New',
    keywords: ['border radius generator', 'rounded corner css', 'css radius tool'],
    intro: 'Explore corner treatments visually instead of guessing pixel values in CSS by hand.',
    useCases: ['Style buttons and cards', 'Create organic shapes', 'Match rounded UI systems'],
    howToSteps: [
      { title: 'Choose the shape', description: 'Start with the element preview you want to round or customize.' },
      { title: 'Adjust each corner', description: 'Use the controls to set matching or asymmetric border-radius values.' },
      { title: 'Copy the CSS', description: 'Paste the generated border-radius output into your styles.' },
    ],
  }),
  createToolDefinition({
    id: 'css-animation-generator',
    name: 'CSS Animation Generator',
    description: 'Build keyframe animations visually with presets, timing controls, and live preview.',
    category: 'Design',
    categoryColor: 'amber',
    icon: 'PlayCircleIcon',
    gradient: 'from-pink-500 to-fuchsia-600',
    isNew: true,
    badge: 'New',
    keywords: ['css animation generator', 'keyframe builder', 'animation css tool'],
    intro: 'Create motion effects with a faster feedback loop before shipping them into product UI or landing pages.',
    useCases: ['Animate buttons and cards', 'Prototype motion presets', 'Generate CSS keyframes for interfaces'],
    howToSteps: [
      { title: 'Pick an animation direction', description: 'Start from a preset or create a motion sequence from scratch.' },
      { title: 'Adjust timing and transforms', description: 'Tune duration, easing, delay, and transform values in the live preview.' },
      { title: 'Export the keyframes', description: 'Copy the generated CSS animation code and apply it to your element.' },
    ],
  }),
  createToolDefinition({
    id: 'json-formatter',
    name: 'JSON Formatter',
    description: 'Paste JSON to format, minify, and validate with error highlighting and stats.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'CodeBracketSquareIcon',
    gradient: 'from-green-500 to-emerald-600',
    isNew: true,
    badge: 'New',
    keywords: ['json formatter', 'json validator', 'json minifier'],
    intro: 'Clean up API payloads and debug JSON structures faster during development, testing, and documentation work.',
    useCases: ['Validate API responses', 'Minify JSON payloads', 'Format data for debugging or docs'],
    howToSteps: [
      { title: 'Paste your JSON', description: 'Drop in the JSON payload, object, or response you want to inspect.' },
      { title: 'Format or validate it', description: 'Use the formatter to clean the structure and catch invalid syntax quickly.' },
      { title: 'Copy the result', description: 'Reuse the formatted or minified JSON in code, tests, or documentation.' },
    ],
  }),
  createToolDefinition({
    id: 'base64',
    name: 'Base64 Encoder/Decoder',
    description: 'Encode or decode text and images to/from Base64 instantly in-browser.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'ArrowsRightLeftIcon',
    gradient: 'from-teal-500 to-cyan-600',
    isNew: true,
    badge: 'New',
    keywords: ['base64 encoder', 'base64 decoder', 'image to base64'],
    intro: 'Convert raw text or files into Base64 and back during API, embedding, and testing workflows.',
    useCases: ['Encode small assets', 'Decode API payloads', 'Test Base64 handling in apps'],
    howToSteps: [
      { title: 'Choose encode or decode', description: 'Pick the direction you need before adding your content or file.' },
      { title: 'Paste text or upload data', description: 'Provide the string or image that should be transformed by the tool.' },
      { title: 'Copy the output', description: 'Use the generated Base64 or decoded result in your project or debugging flow.' },
    ],
  }),
  createToolDefinition({
    id: 'regex-tester',
    name: 'Regex Tester',
    description: 'Live regex matching with match highlighting, group capture, and replace mode.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'MagnifyingGlassIcon',
    gradient: 'from-orange-500 to-amber-600',
    isNew: true,
    badge: 'New',
    keywords: ['regex tester', 'regular expression tool', 'pattern matching tester'],
    intro: 'Build and debug regular expressions with faster visual feedback and less trial-and-error.',
    useCases: ['Test validation rules', 'Extract string patterns', 'Prototype replace expressions'],
    howToSteps: [
      { title: 'Add your regex pattern', description: 'Enter the expression and any flags you want to test.' },
      { title: 'Paste sample text', description: 'Use real example content so you can see what the pattern matches.' },
      { title: 'Inspect matches and groups', description: 'Review highlights, captured groups, or replacement results before using the regex in code.' },
    ],
  }),
  createToolDefinition({
    id: 'css-minifier',
    name: 'CSS Minifier',
    description: 'Paste CSS to minify, removing comments, whitespace, and redundant semicolons.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'BoltIcon',
    gradient: 'from-yellow-500 to-orange-500',
    isNew: true,
    badge: 'New',
    keywords: ['css minifier', 'compress css', 'minify stylesheet'],
    intro: 'Reduce stylesheet size quickly for delivery, testing, or code-sharing without leaving the browser.',
    useCases: ['Minify small CSS snippets', 'Prepare embed code', 'Compress styles for quick sharing'],
    howToSteps: [
      { title: 'Paste your stylesheet', description: 'Add the CSS rules or snippet you want to compress.' },
      { title: 'Run the minifier', description: 'Let the tool strip comments and unnecessary whitespace from the code.' },
      { title: 'Copy the compact CSS', description: 'Use the minified output wherever a smaller stylesheet is useful.' },
    ],
  }),
  createToolDefinition({
    id: 'password-generator',
    name: 'Password Generator',
    description: 'Generate secure passwords with custom rules, strength meter, and bulk generation.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'KeyIcon',
    gradient: 'from-red-500 to-rose-600',
    isNew: true,
    badge: 'New',
    keywords: ['password generator', 'secure password maker', 'random password'],
    intro: 'Create stronger passwords faster for personal accounts, team tools, and internal credential rotation.',
    useCases: ['Generate secure credentials', 'Create bulk passwords', 'Test password rules'],
    howToSteps: [
      { title: 'Choose password rules', description: 'Set the desired length and character types such as symbols, numbers, or mixed case.' },
      { title: 'Review the generated password', description: 'Check the strength score and regenerate until it matches your requirements.' },
      { title: 'Copy and store it safely', description: 'Use the password in your account setup flow or add it to a password manager.' },
    ],
  }),
  createToolDefinition({
    id: 'hash-generator',
    name: 'Hash Generator',
    description: 'Generate MD5, SHA-1, SHA-256, and SHA-512 hashes from any text input.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'CpuChipIcon',
    gradient: 'from-violet-500 to-purple-600',
    isNew: true,
    badge: 'New',
    keywords: ['hash generator', 'sha256 generator', 'md5 hash', 'sha512 tool'],
    intro: 'Create hashes quickly for checksums, testing, debugging, and simple integrity verification workflows.',
    useCases: ['Generate file or text hashes', 'Test backend hashing outputs', 'Compare values during debugging'],
    howToSteps: [
      { title: 'Enter the source text', description: 'Paste the exact string or value you want to hash.' },
      { title: 'Select the algorithm', description: 'Choose MD5, SHA-1, SHA-256, or SHA-512 depending on your use case.' },
      { title: 'Copy the hash output', description: 'Use the generated hash in your test data, comparisons, or verification flow.' },
    ],
  }),
  createToolDefinition({
    id: 'markdown-editor',
    name: 'Markdown Editor',
    description: 'Split-pane live preview Markdown editor with HTML export and download.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'PencilSquareIcon',
    gradient: 'from-blue-500 to-indigo-600',
    isNew: true,
    badge: 'New',
    keywords: ['markdown editor', 'markdown preview', 'markdown to html'],
    intro: 'Write documentation, notes, and content drafts with a faster editing and preview loop.',
    useCases: ['Draft README files', 'Preview Markdown docs', 'Export Markdown to HTML'],
    howToSteps: [
      { title: 'Write or paste Markdown', description: 'Enter the content you want to edit and preview in the workspace.' },
      { title: 'Review the live rendering', description: 'Use the split preview to confirm headings, lists, links, and formatting look right.' },
      { title: 'Export the result', description: 'Copy the Markdown or generated HTML and publish it where you need it.' },
    ],
  }),
  createToolDefinition({
    id: 'url-encoder',
    name: 'URL Encoder/Decoder',
    description: 'Encode or decode URLs and URI components with encodeURIComponent or encodeURI.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'LinkIcon',
    gradient: 'from-sky-500 to-blue-600',
    isNew: true,
    badge: 'New',
    keywords: ['url encoder', 'url decoder', 'encode uri component'],
    intro: 'Handle query strings and URL-safe values faster when testing APIs, redirects, and app routing.',
    useCases: ['Encode query parameters', 'Decode URLs from logs', 'Prepare redirect or share links'],
    howToSteps: [
      { title: 'Choose encode or decode mode', description: 'Select whether the input should be transformed into a safe URL string or decoded back.' },
      { title: 'Paste the URL value', description: 'Enter the link, query string, or component that needs conversion.' },
      { title: 'Copy the result', description: 'Use the encoded or decoded output in your app, API requests, or debugging session.' },
    ],
  }),
  createToolDefinition({
    id: 'aspect-ratio-calculator',
    name: 'Aspect Ratio Calculator',
    description: 'Enter dimensions, lock ratio, and get a full responsive size table.',
    category: 'Developer',
    categoryColor: 'green',
    icon: 'RectangleGroupIcon',
    gradient: 'from-cyan-500 to-teal-600',
    isNew: true,
    badge: 'New',
    keywords: ['aspect ratio calculator', 'responsive dimensions', 'image ratio tool'],
    intro: 'Calculate proportional sizes more quickly for media, embeds, screenshots, and responsive layouts.',
    useCases: ['Resize media proportionally', 'Prepare responsive embed sizes', 'Check image or video dimensions'],
    howToSteps: [
      { title: 'Enter the original dimensions', description: 'Start with the known width and height or the ratio you want to preserve.' },
      { title: 'Change one size value', description: 'Update the width or height and let the calculator keep the ratio locked.' },
      { title: 'Use the generated sizes', description: 'Apply the resulting measurements in your layout, media, or export workflow.' },
    ],
  }),
];

export const toolCategories = ['Generators', 'PDF', 'Image', 'Design', 'Developer'] as const;

export function getToolBySlug(slug: string) {
  return toolDefinitions.find((tool) => tool.slug === slug);
}

export function getToolById(id: string) {
  return toolDefinitions.find((tool) => tool.id === id);
}

export function getToolHref(slug: string) {
  return `/tools/${slug}`;
}

export function getRelatedTools(slug: string, limit = 3) {
  const tool = getToolBySlug(slug);

  if (!tool) {
    return [];
  }

  return toolDefinitions
    .filter((candidate) => candidate.slug !== slug && candidate.category === tool.category)
    .slice(0, limit);
}
